import React, { useCallback, useEffect } from 'react'
import { Button, IconButton } from 'components'

import Menu, { menuItems } from './Menu'
import Total from './Total'
import { Box, Divider, Typography } from '@mui/material'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useOrderStore } from 'modules/orders/store'
import { yupResolver } from '@hookform/resolvers/yup'
import { schema } from './schema'
import { normalizeOrderForm } from './normalizeData'
import { useIsFetching } from 'react-query'
import { RecalculeButton } from './RecalculeButton'
import { MdKeyboardArrowLeft } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'
import { DuplicateOrderRecalcHandler } from './DuplicateOrderRecalcHandler'

export type MainRef = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLElement>,
  HTMLElement
>

export const LEFT_SIDE_WIDTH = 300

const menuItemsLen = menuItems.length

const DirectionalButtons = ({
  handleSubmit: submit,
  isSubmitting = false,
  isEdit = false
}: any) => {
  const currentStep = useOrderStore.useCurrentStep()
  const setCurrentStep = useOrderStore.useSetCurrentStep()
  const form = useOrderStore.useForm()
  const setForm = useOrderStore.useSetForm()
  const { handleSubmit, trigger, formState, getValues } = useFormContext()
  const isFetching = useIsFetching()
  const isTaxesRecalculePending = useOrderStore.useIsTaxesRecalculePending()

  const getCurrentStep = useCallback(
    () => menuItems.map((item) => item.id).indexOf(currentStep),
    [currentStep]
  )

  const isLastStep = menuItemsLen - 1 === getCurrentStep()

  const handleNextClick = async () => {
    const currentStepIndex = getCurrentStep()

    const isValid = await trigger(menuItems[currentStepIndex].fields)

    if (currentStepIndex < menuItemsLen - 1 && isValid) {
      menuItems[currentStepIndex].fields.forEach((field) => {
        setForm({
          [field]: getValues(field)
        })
      })
      setCurrentStep(menuItems[currentStepIndex + 1].id)
    }
  }

  const handlePreviousClick = async () => {
    const currentStepIndex = getCurrentStep()

    if (currentStepIndex > 0) {
      menuItems[currentStepIndex].fields.forEach((field) => {
        setForm({
          [field]: getValues(field)
        })
      })
      setCurrentStep(menuItems[currentStepIndex - 1].id)
    }
  }

  const onSubmit = (data: any) => {
    const newData = normalizeOrderForm({
      ...data,
      proposalId: form.orderId,
      items: form.orderItems,
      total: form.total
    })
    submit(newData)
  }

  const hasError = Object.keys(formState?.errors).length > 0

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        px: 3,
        py: 1.5
      }}
    >
      <Button
        onClick={handlePreviousClick}
        disabled={!!isFetching}
        variant="text"
      >
        Voltar
      </Button>
      {!isLastStep && (
        <Button onClick={handleNextClick} disabled={!!isFetching}>
          Próximo
        </Button>
      )}
      {isLastStep && !isTaxesRecalculePending && (
        <Button
          color="success"
          disabled={isSubmitting || !!isFetching || hasError}
          onClick={handleSubmit(onSubmit)}
        >
          {isEdit ? 'Atualizar' : 'Criar'} Orçamento
        </Button>
      )}
      {isLastStep && isTaxesRecalculePending && <RecalculeButton />}
    </Box>
  )
}

export const Form = (props: any) => {
  const { defaultValues } = props
  const navigate = useNavigate()
  const pageLocation = useLocation()
  const isDuplicatePage = pageLocation.pathname.includes('/duplicate')

  const currentStep = useOrderStore.useCurrentStep()
  const setCurrentStep = useOrderStore.useSetCurrentStep()

  const methods = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    defaultValues: {
      entryValue: '0',
      entryPercent: '0',
      deliveryDate: new Date().toISOString(),
      orderItems: [],
      unity: null,
      seller: null,
      indicator: null,
      customer: null,
      orderType: null,
      purchaseOrder: '',
      representantOrderNumber: '',
      requestedDate: null,
      opportunity: null,
      saleChannel: null,
      freightType: null,
      freightPrice: '',
      expeditionObs: '',
      totalDiscount: '0',
      totalExpenses: '0',
      commissionPercent: 0,
      paymentTerm: null,
      installments: [],
      observation: '',
      printObs: 0,
      paymentResponsible: null,
      hasDirtRoadQuiz: null,
      dirtRoadQuiz1: '',
      dirtRoadQuiz2: null,
      dirtRoadQuiz3: null,
      dirtRoadQuiz4: null,
      cdPedidoReserva: null,
      crossSale: null
    }
  })

  useEffect(() => {
    if (defaultValues) {
      methods.reset(defaultValues)
    }
  }, [defaultValues, methods])

  useEffect(() => {
    if (!currentStep) {
      setCurrentStep(menuItems[0].id)
    }
  }, [currentStep, setCurrentStep])

  return (
    <FormProvider {...methods}>
      {isDuplicatePage && <DuplicateOrderRecalcHandler />}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 0,
          right: 16,
          bottom: 0,
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: '#fff'
        }}
      >
        {/* Sidebar fixa */}
        <Box
          sx={{
            width: LEFT_SIDE_WIDTH,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              py: 1,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
            }}
          >
            <IconButton
              onClick={() => {
                const msg = 'Há um pedido em andamento, será perdido ao voltar!'
                if (confirm(msg)) {
                  navigate('/orders')
                }
              }}
              sx={{ mr: 1 }}
            >
              <MdKeyboardArrowLeft size={24} color="#565656" />
            </IconButton>
            <Typography variant="body2" sx={{ color: '#565656' }}>
              Voltar
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Menu />
          </Box>
          <Divider />
          <Total />
        </Box>

        {/* Área direita: conteúdo + botões */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          {/* Conteúdo do step com scroll interno */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 4,
              py: 2
            }}
          >
            {menuItems.map((item) => {
              if (item.id !== currentStep) return null
              const Component = item.Component
              return <Component key={item.id} />
            })}
          </Box>

          {/* Botões fixos no bottom */}
          <Box
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: '#fff',
              flexShrink: 0
            }}
          >
            <DirectionalButtons {...props} />
          </Box>
        </Box>
      </Box>
    </FormProvider>
  )
}
